/**
 * Oil & Gas — Upstream Exploration  (standalone module)
 *
 * Magnetometry survey management and hyperspectral / seismic overlay analysis
 * for upstream prospect de-risking. Enables operators to ingest survey data,
 * view AI-generated structural interpretations, and score prospect viability.
 */
import React, { useState, useEffect } from 'react';
import {
  Search, Layers, BarChart2, MapPin, PlusCircle,
  RefreshCw, ChevronDown, ChevronUp, Activity,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExploreSurvey {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  metadata?: {
    survey_type?: string;
    basin_name?: string;
    prospect_name?: string;
    area_km2?: number;
    ai_analysis_status?: string;
    structural_interpretation?: string;
    viability_score?: number;
    depth_target_m?: number;
    vertical?: string;
    [key: string]: any;
  };
}

// ─── Survey type config ───────────────────────────────────────────────────────

const SURVEY_TYPES = [
  { value: 'magnetometry',    label: 'Aeromagnetic Survey' },
  { value: 'hyperspectral',   label: 'Hyperspectral Imaging' },
  { value: 'seismic_2d',      label: 'Seismic 2D' },
  { value: 'seismic_3d',      label: 'Seismic 3D' },
  { value: 'gravity',         label: 'Gravity Survey' },
];

const VIABILITY_COLOUR = (score: number) => {
  if (score >= 70) return 'text-green-700 dark:text-green-400';
  if (score >= 40) return 'text-amber-700 dark:text-amber-400';
  return 'text-red-700 dark:text-red-400';
};

// ─── Component ────────────────────────────────────────────────────────────────

export const UpstreamExplorationPage: React.FC = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys]         = useState<ExploreSurvey[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [showForm, setShowForm]       = useState(false);
  const [saving, setSaving]           = useState(false);

  // New survey form
  const [form, setForm] = useState({
    name: '', survey_type: 'magnetometry', basin_name: '',
    prospect_name: '', area_km2: '', depth_target_m: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { mission_type: 'exploration_survey', limit: 50 } });
      setSurveys((res.data?.missions ?? []).filter((m: ExploreSurvey) => m.metadata?.vertical === 'oil_gas'));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name.trim() || !form.basin_name.trim()) {
      setError('Survey name and basin name are required');
      return;
    }
    setSaving(true); setError(null);
    try {
      await api.post('/missions', {
        name:         form.name.trim(),
        mission_type: 'exploration_survey',
        metadata: {
          survey_type:    form.survey_type,
          basin_name:     form.basin_name.trim(),
          prospect_name:  form.prospect_name.trim() || undefined,
          area_km2:       form.area_km2 ? Number(form.area_km2) : undefined,
          depth_target_m: form.depth_target_m ? Number(form.depth_target_m) : undefined,
          vertical:       'oil_gas',
        },
      });
      setSuccess(`Survey "${form.name}" registered`);
      setForm({ name: '', survey_type: 'magnetometry', basin_name: '', prospect_name: '', area_km2: '', depth_target_m: '' });
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  const INPUT_CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const SELECT_CLS = INPUT_CLS;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Search className="w-7 h-7 text-orange-500" /> Upstream Exploration
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Magnetometry, hyperspectral, and seismic survey management — AI-assisted prospect de-risking
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => setShowForm(!showForm)} variant={showForm ? 'outline' : 'primary'}>
            {showForm ? 'Cancel' : <><PlusCircle className="w-4 h-4 mr-1" /> New Survey</>}
          </Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── New survey form ── */}
      {showForm && (
        <Card>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">Register Exploration Survey</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Survey Name *</label>
              <input className={INPUT_CLS} placeholder="e.g. OPL-226 Aeromagnetic Block Survey"
                value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Survey Type</label>
              <select className={SELECT_CLS} value={form.survey_type}
                onChange={(e) => setForm((p) => ({ ...p, survey_type: e.target.value }))}>
                {SURVEY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Basin Name *</label>
              <input className={INPUT_CLS} placeholder="e.g. Niger Delta, Saharan Platform"
                value={form.basin_name} onChange={(e) => setForm((p) => ({ ...p, basin_name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prospect Name</label>
              <input className={INPUT_CLS} placeholder="e.g. Prospect Alpha-7"
                value={form.prospect_name} onChange={(e) => setForm((p) => ({ ...p, prospect_name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Survey Area (km²)</label>
              <input className={INPUT_CLS} type="number" min="1" placeholder="e.g. 250"
                value={form.area_km2} onChange={(e) => setForm((p) => ({ ...p, area_km2: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Depth (m)</label>
              <input className={INPUT_CLS} type="number" min="100" placeholder="e.g. 3500"
                value={form.depth_target_m} onChange={(e) => setForm((p) => ({ ...p, depth_target_m: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button onClick={handleSave} loading={saving}>Register Survey</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* ── Survey list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {surveys.length} exploration survey{surveys.length !== 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-14">
            <Layers className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No exploration surveys registered</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>New Survey</strong> to register a block</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {surveys.map((s) => {
              const isExpanded = expandedId === s.id;
              const score      = s.metadata?.viability_score;
              return (
                <div key={s.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : s.id)}
                  >
                    <MapPin className="w-4 h-4 flex-shrink-0 text-orange-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {s.metadata?.basin_name} · {SURVEY_TYPES.find((t) => t.value === s.metadata?.survey_type)?.label ?? s.metadata?.survey_type}
                      </p>
                    </div>
                    {score != null && (
                      <div className="text-right flex-shrink-0">
                        <p className={`text-lg font-bold ${VIABILITY_COLOUR(score)}`}>{score}</p>
                        <p className="text-xs text-gray-400">viability</p>
                      </div>
                    )}
                    {s.metadata?.ai_analysis_status === 'complete' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium flex-shrink-0">
                        AI Ready
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        {[
                          { label: 'Basin', value: s.metadata?.basin_name },
                          { label: 'Prospect', value: s.metadata?.prospect_name },
                          { label: 'Area', value: s.metadata?.area_km2 != null ? `${s.metadata.area_km2} km²` : undefined },
                          { label: 'Target Depth', value: s.metadata?.depth_target_m != null ? `${s.metadata.depth_target_m} m` : undefined },
                          { label: 'Registered', value: new Date(s.created_at).toLocaleDateString('en-GB') },
                        ].filter((r) => r.value).map((r) => (
                          <div key={r.label}>
                            <p className="text-gray-500 dark:text-gray-400">{r.label}</p>
                            <p className="font-medium text-gray-800 dark:text-gray-200">{r.value}</p>
                          </div>
                        ))}
                      </div>

                      {s.metadata?.structural_interpretation && (
                        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1">
                            <Activity className="w-3.5 h-3.5" /> AI Structural Interpretation
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{s.metadata.structural_interpretation}</p>
                        </div>
                      )}

                      <Button size="sm" variant="outline" onClick={() => navigate(`/missions/${s.id}`)}>
                        Open Survey Record
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default UpstreamExplorationPage;

// Made with Bob
